import { useEffect, useRef } from 'react';

const experiences = [
  {
    number: '01',
    title: 'O Despertar',
    day: '1º Dia — Noite de Gala',
    duration: '30 min',
    speaker: 'Dafne Borsatti',
    description:
      'Abertura com Dafne Borsatti. Conversa crua sobre liderar onde ninguém esperava você. Sem clichês, sem filtro.',
    image:
      'https://storage.readdy-site.link/project_files/95ab1d24-f5e3-49af-acf5-f9d2f5689cc7/c09fe1ea-5401-4881-a034-1c98a7ae6cb3_Screen-Shot-2026-04-09-at-3.39.04-PM.png?v=7aafb54f93cd88dd61d7c0eb922b3da5',
    imagePosition: '50% 20%',
  },
  {
    number: '02',
    title: 'As Leoas Falam',
    day: '2º Dia — Palestra',
    duration: '90 min',
    speaker: 'Mediação: Nikole Fernan',
    description:
      'Painel com 4–6 executivas de alta performance. 12 minutos cada. Regra: nada de PowerPoint. Só verdade.',
    image:
      'https://storage.readdy-site.link/project_files/95ab1d24-f5e3-49af-acf5-f9d2f5689cc7/d0e40dba-61a5-4bcf-b286-72741a89e078_Screen-Shot-2026-04-09-at-3.39.27-PM.png?v=40bd695011e2aff87a22835c65c931bd',
    imagePosition: '50% 20%',
  },
  {
    number: '03',
    title: 'A Mesa',
    day: '3º Dia — Roda de Negócios',
    duration: '60 min',
    speaker: 'Networking Estruturado',
    description:
      'Mesas temáticas com agentes convidados: Finanças, Expansão, Liderança, Marketing, Operações. O objetivo: sair com conexão que vire negócio em 90 dias.',
    image:
      'https://storage.readdy-site.link/project_files/95ab1d24-f5e3-49af-acf5-f9d2f5689cc7/9b78d76f-fa06-461b-8e5e-68e1d24beace_Screen-Shot-2026-03-26-at-11.03.29-AM.png?v=e7af189dabee9e8feb9532edd0f5a1ab',
    imagePosition: '50% 20%',
  },
  {
    number: '04',
    title: 'O Rugido',
    day: 'Encerramento',
    duration: '30 min',
    speaker: 'Encerramento Coletivo',
    description:
      'Cada participante escreve um compromisso com prazo. Leitura em voz alta. Rugido coletivo. Foto oficial. Brinde.',
    image:
      'https://storage.readdy-site.link/project_files/95ab1d24-f5e3-49af-acf5-f9d2f5689cc7/3408547b-3aa7-458b-be8b-25e7930e1979_Screen-Shot-2026-03-26-at-11.03.03-AM.png?v=019805f9af7df9a1442097fca66cf127',
    imagePosition: '50% 20%',
  },
];

export default function ExperienceSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-target').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 140);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="experiencia"
      ref={sectionRef}
      className="bg-obsidian py-28 md:py-36 px-6 relative overflow-hidden"
    >
      {/* Top gold line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gold/30" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="animate-target opacity-0 transition-all duration-700 mb-4" style={{ transform: 'translateY(30px)' }}>
            <span className="font-sans text-xs tracking-widest3 uppercase text-gold" style={{ letterSpacing: '0.3em' }}>
              A Experiência
            </span>
          </div>
          <div className="animate-target opacity-0 transition-all duration-700 mb-5" style={{ transform: 'translateY(30px)' }}>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-cream">
              Uma experiência, não uma palestra.
            </h2>
          </div>
          <div className="animate-target opacity-0 transition-all duration-700 w-16 h-px bg-gold mx-auto mb-6" style={{ transform: 'translateY(30px)' }} />
          <p className="animate-target opacity-0 transition-all duration-700 font-sans text-sm md:text-base text-cream/50 max-w-xl mx-auto font-light" style={{ transform: 'translateY(30px)' }}>
            Quatro blocos. Uma jornada. Da reflexão à ação, do insight ao contrato.
          </p>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {experiences.map((exp, index) => (
            <div
              key={exp.number}
              className="animate-target opacity-0 transition-all duration-700 group border border-gold/20 hover:border-gold/50 overflow-hidden"
              style={{ transform: 'translateY(30px)', transitionDelay: `${index * 100}ms` }}
            >
              {/* Image */}
              <div className="w-full h-52 overflow-hidden">
                <img
                  src={exp.image}
                  alt={exp.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  style={{ objectPosition: exp.imagePosition }}
                />
              </div>

              {/* Content */}
              <div className="p-8">
                <div className="flex items-start justify-between mb-2">
                  <span className="font-serif text-4xl font-bold text-gold/30 leading-none">{exp.number}</span>
                  <span className="font-sans text-xs tracking-widest uppercase text-gold/60 border border-gold/30 px-3 py-1 whitespace-nowrap" style={{ letterSpacing: '0.2em' }}>
                    {exp.duration}
                  </span>
                </div>
                <p className="font-sans text-xs text-gold/50 uppercase mb-3" style={{ letterSpacing: '0.2em' }}>
                  {exp.day}
                </p>
                <h3 className="font-serif text-xl font-bold text-cream mb-2 uppercase tracking-wide">
                  {exp.title}
                </h3>
                <div className="w-8 h-px bg-gold mb-4" />
                <p className="font-sans text-xs tracking-wide text-gold/70 uppercase mb-4" style={{ letterSpacing: '0.15em' }}>
                  {exp.speaker}
                </p>
                <p className="font-sans text-sm text-cream/60 leading-relaxed font-light">
                  {exp.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer stats */}
        <div className="animate-target opacity-0 transition-all duration-700 border-t border-gold/20 pt-10" style={{ transform: 'translateY(30px)' }}>
          <div className="flex flex-wrap justify-center gap-10 md:gap-16 text-center">
            {[
              { value: '4 horas', label: 'de imersão' },
              { value: 'Presencial', label: 'experiência ao vivo' },
              { value: '100–200', label: 'participantes' },
              { value: 'Exclusivo', label: 'curadoria de presença' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-serif text-lg font-bold text-gold">{stat.value}</p>
                <p className="font-sans text-xs text-cream/40 uppercase tracking-widest mt-1" style={{ letterSpacing: '0.2em' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom gold line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gold/30" />
    </section>
  );
}
